/**
 * Demo pre-run editor card.
 *
 * Phase 2 of the 3-tier asset flow plan. One big drop zone, N rows
 * below. The demoer drags whatever buyer assets they want into the
 * zone; each upload creates / replaces a pre-run row keyed by
 * filename. For each row the operator can:
 *   1. Watch the slot flip from "Running pre-run…" → "Ready" once
 *      the real-AI ReviewWorkflow completes, or → "Pre-run failed"
 *      when real AI didn't produce actionable findings. Per the
 *      NO FALLBACKS POLICY there is no synthesised / fallback
 *      "Ready" state — the row either ran real AI successfully or
 *      it errored, and the operator must Re-run AI to clear it.
 *   2. Edit the resulting findings (text, severity, source citation,
 *      pin coordinates) before saving.
 *   3. Clear a row to free it up.
 *
 * MG-role only. Backend gates all four endpoints on
 * `UserService.hasMeaningfulGigsRole` + `isDemoEnterpriseId`.
 *
 * Autonomous-decision note: the plan specifies a pin-on-thumbnail
 * editor; here we render pin coordinates as numeric inputs (`rel_x`,
 * `rel_y` between 0 and 1) so the demoer can still move pins without
 * us building a tldraw / canvas overlay. The killer-flow streamer
 * reads `rel_x` / `rel_y` identically either way.
 */
import { Plus, Trash, Upload } from "@phosphor-icons/react";
import {
  Badge,
  Box,
  Button,
  Callout,
  Card,
  Flex,
  Heading,
  IconButton,
  Select,
  Text,
  TextArea,
  TextField,
} from "@radix-ui/themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  type DemoPrerunAsset,
  type DemoPrerunFinding,
  type DemoPrerunRiskScore,
  type DemoReviewFunction,
  deleteDemoPrerun,
  DEMO_REVIEW_FUNCTION_LABELS,
  listDemoPrerunAssets,
  retryDemoPrerun,
  startDemoPrerun,
  updateDemoPrerun,
} from "../../../services/demo";
import { uploadFile } from "../../../services/upload";
import { errorMessage } from "../../../utils/http";

const QUERY_KEY = ["demo", "prerun-assets"] as const;

export function DemoPrerunCard() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: listDemoPrerunAssets,
    // Pending rows reconcile against Temporal on every read — keep
    // the refetch tight so "Running pre-run…" flips to "Ready" within
    // a few seconds of the workflow completing.
    refetchInterval: (q) => {
      const assets = q.state.data?.assets ?? [];
      return assets.some((a) => a.state === "pending") ? 4000 : false;
    },
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  return (
    <Card data-testid="settings-card-demo-prerun">
      <Flex direction="column" gap="4">
        <Flex direction="column" gap="1">
          <Heading as="h2" size="3" weight="bold">
            Pre-load the buyer&apos;s files
          </Heading>
          <Text size="2" color="gray">
            Before a meeting, upload one or more of the buyer&apos;s real files
            here. Each upload runs the AI review on it now (in the background)
            and saves the findings. You can read and edit the findings before
            the meeting. Then, when you drag the same file into Ask Puntt during
            the demo, it replays those findings instantly so the buyer sees the
            same 90-second moment with their own work instead of the Vitalora
            examples. Reset Demo clears everything you&apos;ve uploaded here.
          </Text>
        </Flex>

        <DropZone onChanged={invalidate} />

        {isLoading ? (
          <Text size="2" color="gray">
            Loading uploaded files…
          </Text>
        ) : error ? (
          <Text size="2" color="red">
            Couldn&apos;t load the uploaded files. Try refreshing.
          </Text>
        ) : (data?.assets ?? []).length === 0 ? (
          <Text size="2" color="gray">
            Nothing uploaded yet. Drop a file above to get started.
          </Text>
        ) : (
          <Flex direction="column" gap="3">
            {(data?.assets ?? []).map((row) => (
              <PrerunAssetRow key={row.id} row={row} onChanged={invalidate} />
            ))}
          </Flex>
        )}
      </Flex>
    </Card>
  );
}

function DropZone({ onChanged }: { onChanged: () => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number }>({
    done: 0,
    total: 0,
  });
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files);
      if (list.length === 0) return;
      setBusy(true);
      setErrorMsg(null);
      setProgress({ done: 0, total: list.length });
      try {
        for (let i = 0; i < list.length; i++) {
          const file = list[i];
          const uploaded = await uploadFile(file);
          const assetType: "image" | "video" | "file" = file.type.startsWith(
            "image/",
          )
            ? "image"
            : file.type.startsWith("video/")
              ? "video"
              : "file";
          await startDemoPrerun({
            s3Key: uploaded.key,
            name: file.name,
            type: assetType,
            thumbnailUrl: uploaded.location,
            size: file.size,
          });
          setProgress({ done: i + 1, total: list.length });
          onChanged();
        }
      } catch (err) {
        setErrorMsg(await errorMessage(err));
      } finally {
        setBusy(false);
        setProgress({ done: 0, total: 0 });
      }
    },
    [onChanged],
  );

  return (
    <Box
      // Whole zone is the drop target. `border-dashed` + the dragging
      // tint replicate the standard "drop here" UX without pulling in
      // a third-party library.
      className={`rounded-md border-2 border-dashed p-6 text-center transition-colors ${
        isDragging
          ? "border-puntt-accent-9 bg-puntt-accent-3"
          : "border-puntt-neutral-gray-5 bg-puntt-neutral-gray-1"
      }`}
      onDragEnter={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        setIsDragging(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer?.files?.length) {
          void handleFiles(e.dataTransfer.files);
        }
      }}
      data-testid="prerun-dropzone"
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          const files = e.target.files;
          e.target.value = "";
          if (files?.length) void handleFiles(files);
        }}
        data-testid="prerun-dropzone-file-input"
      />
      <Flex direction="column" gap="2" align="center">
        <Upload size={24} />
        <Text size="2" weight="medium">
          {busy
            ? `Uploading ${progress.done} of ${progress.total}…`
            : "Drop the buyer's files here, or click to choose them"}
        </Text>
        <Text size="1" color="gray">
          Images, videos, or PDFs. Each file gets its own AI review, run in the
          background.
        </Text>
        <Button
          size="2"
          variant="soft"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid="prerun-dropzone-browse"
        >
          {busy ? "Working…" : "Choose files"}
        </Button>
        {errorMsg ? (
          <Text size="2" color="red">
            {errorMsg}
          </Text>
        ) : null}
      </Flex>
    </Box>
  );
}

function PrerunAssetRow({
  row,
  onChanged,
}: {
  row: DemoPrerunAsset;
  onChanged: () => void;
}) {
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [draft, setDraft] = useState<DemoPrerunFinding[]>(row.findings ?? []);
  const [draftRisk, setDraftRisk] = useState<DemoPrerunRiskScore>(
    row.riskScore ?? "low",
  );
  const [dirty, setDirty] = useState(false);

  const reseatDraft = useCallback((next: DemoPrerunAsset) => {
    setDraft(next.findings ?? []);
    setDraftRisk(next.riskScore ?? "low");
    setDirty(false);
  }, []);

  // When the server-side reconcile lands new findings on the row (via
  // React Query polling), we need to pull them into local `draft` so
  // the UI actually renders them. Without this the row sits "Ready"
  // with an empty findings list because `useState` only seeded `draft`
  // once on mount — at that point the row was still "pending" with
  // findings=[].
  //
  // We only re-seat when the operator hasn't started editing locally
  // (`dirty=false`). If they are mid-edit we leave their draft alone —
  // they can hit Discard to explicitly resync from the server, which
  // is what the existing Discard button already does via reseatDraft.
  //
  // Trigger by JSON-comparing the incoming findings to current draft.
  // It is fine to do a JSON compare on every render here: the list is
  // small (<= a couple dozen findings per asset), and the alternative
  // (referential equality on row.findings) refuses to detect changes
  // because React Query returns a fresh array on every poll even when
  // the contents are identical.
  const serverFindings = row.findings ?? [];
  const serverRisk = row.riskScore ?? "low";
  useEffect(() => {
    if (dirty) return;
    const sameFindings =
      JSON.stringify(serverFindings) === JSON.stringify(draft);
    const sameRisk = serverRisk === draftRisk;
    if (sameFindings && sameRisk) return;
    setDraft(serverFindings);
    setDraftRisk(serverRisk);
  }, [dirty, serverFindings, serverRisk, draft, draftRisk]);

  const updateMutation = useMutation({
    mutationFn: () =>
      updateDemoPrerun(row.id, {
        findings: draft,
        riskScore: draftRisk,
      }),
    onSuccess: () => {
      setDirty(false);
      setErrorMsg(null);
      onChanged();
    },
    onError: async (err) => {
      setErrorMsg(await errorMessage(err));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteDemoPrerun(row.id),
    onSuccess: () => {
      onChanged();
    },
    onError: async (err) => {
      setErrorMsg(await errorMessage(err));
    },
  });

  const retryMutation = useMutation({
    mutationFn: () => retryDemoPrerun(row.id),
    onSuccess: () => {
      setErrorMsg(null);
      onChanged();
    },
    onError: async (err) => {
      setErrorMsg(await errorMessage(err));
    },
  });

  const stateBadge = (
    <Badge
      color={
        row.state === "pending"
          ? "amber"
          : row.state === "error"
            ? "red"
            : dirty
              ? "blue"
              : "green"
      }
      data-testid={`prerun-asset-${row.id}-state`}
    >
      {row.state === "pending"
        ? "AI is reviewing…"
        : row.state === "error"
          ? "Review failed"
          : dirty
            ? "Edited"
            : "Ready"}
    </Badge>
  );

  return (
    <Box
      className="rounded-md border border-puntt-neutral-gray-4 p-3"
      data-testid={`prerun-asset-${row.id}`}
    >
      <Flex direction="column" gap="3">
        <Flex justify="between" align="center">
          <Flex align="center" gap="2" wrap="wrap">
            <Text weight="medium" size="2">
              {row.asset.name}
            </Text>
            {stateBadge}
          </Flex>
          <Flex align="center" gap="2">
            <Button
              size="1"
              variant="soft"
              onClick={() => retryMutation.mutate()}
              disabled={retryMutation.isPending || row.state === "pending"}
              data-testid={`prerun-asset-${row.id}-retry`}
              aria-label="Run the AI review again"
            >
              {retryMutation.isPending ? "Starting…" : "Run review again"}
            </Button>
            <IconButton
              variant="soft"
              color="red"
              size="1"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
              data-testid={`prerun-asset-${row.id}-delete`}
              aria-label="Remove this file"
            >
              <Trash size={14} />
            </IconButton>
          </Flex>
        </Flex>

        <Flex direction="column" gap="1">
          <Text size="1" color="gray">
            <code>{row.asset.type}</code>
            {row.asset.size
              ? ` · ${(row.asset.size / 1024).toFixed(1)} KB`
              : ""}
          </Text>
          {row.jobId ? (
            <Text
              size="1"
              color="gray"
              data-testid={`prerun-asset-${row.id}-job`}
            >
              Job ID: <code style={{ fontSize: "0.85em" }}>{row.jobId}</code>
            </Text>
          ) : null}
        </Flex>

        {row.errorMessage ? (
          <Callout.Root
            color="amber"
            size="1"
            data-testid={`prerun-asset-${row.id}-warning`}
          >
            <Callout.Text>{row.errorMessage}</Callout.Text>
          </Callout.Root>
        ) : null}

        <Flex align="center" gap="2">
          <Text size="2">Overall risk:</Text>
          <Select.Root
            value={draftRisk}
            onValueChange={(v) => {
              setDraftRisk(v as DemoPrerunRiskScore);
              setDirty(true);
            }}
          >
            <Select.Trigger
              data-testid={`prerun-asset-${row.id}-risk-select`}
            />
            <Select.Content>
              <Select.Item value="low">Low</Select.Item>
              <Select.Item value="medium">Medium</Select.Item>
              <Select.Item value="high">High</Select.Item>
            </Select.Content>
          </Select.Root>
        </Flex>

        <Flex direction="column" gap="2">
          {draft.map((finding, idx) => (
            <FindingRow
              key={idx}
              data-testid={`demo-prerun-finding-${row.id}-${idx}`}
              finding={finding}
              rowId={row.id}
              index={idx}
              onChange={(next) => {
                const copy = [...draft];
                copy[idx] = next;
                setDraft(copy);
                setDirty(true);
              }}
              onRemove={() => {
                setDraft(draft.filter((_, i) => i !== idx));
                setDirty(true);
              }}
            />
          ))}
          <Button
            size="1"
            variant="soft"
            color="gray"
            onClick={() => {
              setDraft([
                ...draft,
                {
                  reviewerFunction: "brand",
                  reviewerLabel: "Brand reviewer",
                  rule: "",
                  text: "",
                  source: "",
                  severity: "medium",
                  rel_x: 0.5,
                  rel_y: 0.5,
                },
              ]);
              setDirty(true);
            }}
            data-testid={`prerun-asset-${row.id}-add-finding`}
          >
            <Plus size={14} />
            Add a finding
          </Button>
        </Flex>

        <Flex gap="2" align="center">
          <Button
            onClick={() => updateMutation.mutate()}
            disabled={!dirty || updateMutation.isPending}
            data-testid={`prerun-asset-${row.id}-save`}
          >
            {updateMutation.isPending ? "Saving…" : "Save"}
          </Button>
          <Button
            variant="soft"
            color="gray"
            onClick={() => reseatDraft(row)}
            disabled={!dirty || updateMutation.isPending}
            data-testid={`prerun-asset-${row.id}-discard`}
          >
            Discard
          </Button>
          {errorMsg ? (
            <Text size="2" color="red">
              {errorMsg}
            </Text>
          ) : null}
        </Flex>
      </Flex>
    </Box>
  );
}

function FindingRow({
  finding,
  rowId,
  index,
  onChange,
  onRemove,
}: {
  finding: DemoPrerunFinding;
  rowId: string;
  index: number;
  onChange: (next: DemoPrerunFinding) => void;
  onRemove: () => void;
}) {
  const set = <K extends keyof DemoPrerunFinding>(
    key: K,
    value: DemoPrerunFinding[K],
  ) => onChange({ ...finding, [key]: value });

  return (
    <Box
      className="rounded border border-puntt-neutral-gray-3 bg-puntt-neutral-gray-1 p-2"
      data-testid={`prerun-asset-${rowId}-finding-${index}`}
    >
      <Flex direction="column" gap="2">
        <Flex gap="2" align="center" wrap="wrap">
          <Select.Root
            value={finding.reviewerFunction}
            onValueChange={(v) =>
              set("reviewerFunction", v as DemoReviewFunction)
            }
          >
            <Select.Trigger />
            <Select.Content>
              {(
                ["brand", "agency", "regulatory", "medical", "copy"] as const
              ).map((fn) => (
                <Select.Item key={fn} value={fn}>
                  {DEMO_REVIEW_FUNCTION_LABELS[fn]}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>

          <Select.Root
            value={finding.severity}
            onValueChange={(v) =>
              set("severity", v as DemoPrerunFinding["severity"])
            }
          >
            <Select.Trigger />
            <Select.Content>
              <Select.Item value="high">High</Select.Item>
              <Select.Item value="medium">Medium</Select.Item>
              <Select.Item value="low">Low</Select.Item>
              <Select.Item value="n/a">n/a</Select.Item>
            </Select.Content>
          </Select.Root>

          <IconButton
            variant="soft"
            color="red"
            size="1"
            onClick={onRemove}
            aria-label="Remove this finding"
            data-testid={`prerun-asset-${rowId}-finding-${index}-remove`}
          >
            <Trash size={14} />
          </IconButton>
        </Flex>

        <TextField.Root
          value={finding.reviewerLabel}
          onChange={(e) => set("reviewerLabel", e.target.value)}
          placeholder="Reviewer name (e.g. Brand, Regulatory)"
          data-testid={`prerun-asset-${rowId}-finding-${index}-label`}
        />
        <TextField.Root
          value={finding.rule}
          onChange={(e) => set("rule", e.target.value)}
          placeholder="Which rule was broken"
          data-testid={`prerun-asset-${rowId}-finding-${index}-rule`}
        />
        <TextArea
          value={finding.text}
          onChange={(e) => set("text", e.target.value)}
          placeholder="What the AI found, in plain language"
          rows={2}
          data-testid={`prerun-asset-${rowId}-finding-${index}-text`}
        />
        <TextField.Root
          value={finding.source}
          onChange={(e) => set("source", e.target.value)}
          placeholder="Where the rule comes from (e.g. Brand book §3.2)"
          data-testid={`prerun-asset-${rowId}-finding-${index}-source`}
        />
        <Flex gap="2" align="center">
          <Text size="1" color="gray">
            Pin location on file (0 to 1):
          </Text>
          <TextField.Root
            value={String(finding.rel_x)}
            onChange={(e) => set("rel_x", parseFloat(e.target.value) || 0)}
            placeholder="Across (0 = left, 1 = right)"
            size="1"
            data-testid={`prerun-asset-${rowId}-finding-${index}-relx`}
          />
          <TextField.Root
            value={String(finding.rel_y)}
            onChange={(e) => set("rel_y", parseFloat(e.target.value) || 0)}
            placeholder="Down (0 = top, 1 = bottom)"
            size="1"
            data-testid={`prerun-asset-${rowId}-finding-${index}-rely`}
          />
        </Flex>
      </Flex>
    </Box>
  );
}
